import { SettingUrls } from 'constants/SettingsUrls';
import { useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

export interface UseMarketDrivenShippingMigrationReturn {
    /** True once the merchant has confirmed the migration in this session — flips the banner to its success state. */
    hasMigrated: boolean;
    /** Ids for the banner's "Finish migration"/confirm-modal Cancel buttons to target via commandFor. */
    confirmModalId: string;
    /** Id for the info modal's own Cancel button to target via commandFor. */
    infoModalId: string;
    confirmModalRef: React.RefObject<HTMLElementTagNameMap['s-modal'] | null>;
    infoModalRef: React.RefObject<HTMLElementTagNameMap['s-modal'] | null>;
    /** The confirm modal's "Migrate now" primary action. */
    handleConfirmMigration: () => void;
    /** The info modal's "Go to providers" primary action. */
    handleGoToProviders: () => void;
}

/**
 * Owns the "migrate now" flow's two `s-modal`s for `MarketDrivenShippingMigratedBanner`: the
 * confirm modal, the (still-TODO) migration API call, and the follow-up "what's new" info modal —
 * plus the local `hasMigrated` flag the banner reads to switch to its success state.
 *
 * The modals are shown/hidden imperatively through refs rather than commandFor alone, since
 * confirming has to hide the confirm modal and show the info modal from the same click.
 */
export const useMarketDrivenShippingMigration = (): UseMarketDrivenShippingMigrationReturn => {
    const navigate = useNavigate();

    const confirmModalId = useId();
    const infoModalId = useId();
    const confirmModalRef = useRef<HTMLElementTagNameMap['s-modal']>(null);
    const infoModalRef = useRef<HTMLElementTagNameMap['s-modal']>(null);

    // Local only — there's no migration API yet (see the TODO below), so nothing is persisted.
    const [hasMigrated, setHasMigrated] = useState(false);

    const handleConfirmMigration = () => {
        confirmModalRef.current?.hideOverlay();
        // TODO: point this at the real migration API once it exists.
        setHasMigrated(true);
        infoModalRef.current?.showOverlay();
    };

    const handleGoToProviders = () => {
        infoModalRef.current?.hideOverlay();
        navigate(SettingUrls.shippingProviders);
    };

    return {
        hasMigrated,
        confirmModalId,
        infoModalId,
        confirmModalRef,
        infoModalRef,
        handleConfirmMigration,
        handleGoToProviders,
    };
};

export default useMarketDrivenShippingMigration;
